import React from 'react';
import { FullscreenOverlayRoot } from './FullscreenOverlayRoot';
import { FullscreenOverlayTrigger } from './FullscreenOverlayTrigger';
import { FullscreenOverlayPortal } from './FullscreenOverlayPortal';
import { FullscreenOverlayBackdrop } from './FullscreenOverlayBackdrop';
import { FullscreenOverlayContent } from './FullscreenOverlayContent';
import { FullscreenOverlayHeader } from './FullscreenOverlayHeader';
import { FullscreenOverlayBody } from './FullscreenOverlayBody';
import { FullscreenOverlayFooter } from './FullscreenOverlayFooter';
import type { FullscreenOverlayProps } from './types';

/**
 * FullscreenOverlay Component (Shorthand API)
 *
 * Preserved per HP-7690 audit: the disposition originally called for swapping
 * to Constellation `Modal size="fullScreen"`, but FullscreenOverlay layers
 * substantial product-specific UX over the basic dialog primitive that
 * Constellation Modal does not replicate:
 *
 *   1. Mobile browser-back history (`overlayHistory.ts`) — pushing a
 *      synthetic history entry on open so swipe-back closes the overlay.
 *   2. iOS Safari sibling-display scroll handling (`FullscreenOverlayPortal`)
 *      — hides body siblings while open so the overlay scrolls at document
 *      level, triggering Safari's chrome-retract behavior; then restores on
 *      close with double-rAF correction for chrome-resize race conditions.
 *   3. Nested-overlay support — multiple overlays open at once with
 *      reference-counted scroll lock, fixed-positioned wrappers for nested
 *      overlays on mobile, and stack-aware ESC handling (`overlayStack.ts`).
 *   4. Trigger-time scroll capture (`FullscreenOverlayTrigger`) — saves
 *      `window.scrollY` *before* React re-renders so close-time scroll
 *      restore matches the user's pre-open viewport.
 *   5. Custom focus restoration that defers until *after* scroll restore
 *      (otherwise mobile browsers fight scroll-into-view on focus return).
 *
 * Rebuilding all of that on top of Constellation Modal was estimated as
 * out-of-scope for HP-7690 — the migration is deferred. AC-2 (no `hpx*`
 * tokens) was satisfied by tokenizing the styles file in place.
 *
 * Supports three layout variants:
 * - 'standard': Traditional modal with sticky header + body + footer
 * - 'media': Full-screen media with floating controls (no footer)
 *
 * @example Standard usage (current Spoke behavior)
 * <FullscreenOverlay
 *   variant="standard"
 *   trigger={<Button>Open</Button>}
 *   title="Amenities"
 *   headerActions={<ShareButton />}
 *   footer={<ApplyButton />}
 * >
 *   <AmenitiesContent />
 * </FullscreenOverlay>
 *
 * @example Media usage (immersive pattern)
 * <FullscreenOverlay
 *   variant="media"
 *   trigger={<Button>View Photos</Button>}
 * >
 *   <PropertyPhotos />
 * </FullscreenOverlay>
 */
export const FullscreenOverlay = ({
  variant = 'standard',
  trigger,
  title,
  headerActions,
  headerLayout,
  contentPadding,
  footer,
  footerVariant,
  children,
  open,
  defaultOpen = false,
  onOpenChange,
  containerId,
}: FullscreenOverlayProps) => {
  // Determine defaults based on variant
  const actualHeaderLayout = headerLayout ?? (variant === 'standard' ? 'sticky' : 'floating');
  const actualContentPadding = contentPadding ?? (variant === 'media' ? 'edge-to-edge' : 'standard');
  const actualFooterVariant = footerVariant ?? 'equal-width';

  return (
    <FullscreenOverlayRoot open={open} defaultOpen={defaultOpen} onOpenChange={onOpenChange}>
      <FullscreenOverlayTrigger asChild>{trigger}</FullscreenOverlayTrigger>

      <FullscreenOverlayPortal containerId={containerId}>
        <FullscreenOverlayBackdrop>
          <FullscreenOverlayContent variant={variant}>
            <FullscreenOverlayHeader
              title={title}
              actions={headerActions}
              layout={actualHeaderLayout}
              variant={variant}
            />

            <FullscreenOverlayBody padding={actualContentPadding} variant={variant}>
              {children}
            </FullscreenOverlayBody>

            {footer && (
              <FullscreenOverlayFooter variant={actualFooterVariant} overlayVariant={variant}>
                {footer}
              </FullscreenOverlayFooter>
            )}
          </FullscreenOverlayContent>
        </FullscreenOverlayBackdrop>
      </FullscreenOverlayPortal>
    </FullscreenOverlayRoot>
  );
};
